/**
 * One set of formatters for everything Aroo shows (audit S12). UK formats, never ISO in the UI:
 *
 *   formatDate          "10 Oct 2026"
 *   formatWeekdayDate   "Fri 10 Oct 2026"
 *   formatDayMonth      "Fri 10 Oct" (adds the year when it isn't this year)
 *   formatMonthYear     "Oct 2026"          formatMonthYearLong  "October 2026"
 *   formatDateTime      "10 Oct 2026, 14:05" (London time)
 *   formatTime          "14:05"
 *   formatDateRange     "Mon 19 – Fri 23 Oct 2026"
 *   formatNumber        "30,591"            formatHours          "30,591.36"
 *   formatMoney         "£1,250.00"         formatPercent        "83%"
 *
 * Dates accept a calendar date ("2026-10-10", read as that day with no timezone shift), an ISO
 * instant ("2026-10-10T13:05:00Z", shown in London time) or a Date. Missing values become
 * `EMPTY` ("–"), the one empty-cell mark.
 */

export const EMPTY = '–';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export type DateLike = string | Date | null | undefined;

interface Parts {
  y: number;
  m: number; // 1–12
  d: number;
  /** Day of week, 0 = Sunday. */
  dow: number;
  hh: number;
  mm: number;
  hasTime: boolean;
}

const LONDON = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function partsOfInstant(date: Date): Parts {
  const out: Record<string, number> = {};
  for (const p of LONDON.formatToParts(date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  const y = out.year ?? date.getFullYear();
  const m = out.month ?? date.getMonth() + 1;
  const d = out.day ?? date.getDate();
  return {
    y,
    m,
    d,
    dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay(),
    hh: out.hour ?? 0,
    mm: out.minute ?? 0,
    hasTime: true,
  };
}

/** Calendar parts of a date-like value, or null when it isn't a date. */
export function dateParts(value: DateLike): Parts | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    // A Date from a picker or `new Date()` is a local calendar day.
    return {
      y: value.getFullYear(),
      m: value.getMonth() + 1,
      d: value.getDate(),
      dow: value.getDay(),
      hh: value.getHours(),
      mm: value.getMinutes(),
      hasTime: true,
    };
  }
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const m = Number(dateOnly[2]);
    const d = Number(dateOnly[3]);
    return {
      y,
      m,
      d,
      dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay(),
      hh: 0,
      mm: 0,
      hasTime: false,
    };
  }
  // Wall-clock "YYYY-MM-DDTHH:MM[:SS]" with no zone: already local (London) time.
  const wall = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value);
  if (wall) {
    const y = Number(wall[1]);
    const m = Number(wall[2]);
    const d = Number(wall[3]);
    return {
      y,
      m,
      d,
      dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay(),
      hh: Number(wall[4]),
      mm: Number(wall[5]),
      hasTime: true,
    };
  }
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return partsOfInstant(instant);
}

const pad = (n: number) => String(n).padStart(2, '0');

function currentYear(now: Date = new Date()): number {
  return now.getFullYear();
}

/** "10 Oct 2026" */
export function formatDate(value: DateLike, empty = EMPTY): string {
  const p = dateParts(value);
  if (!p) return typeof value === 'string' && value ? value : empty;
  return `${p.d} ${MONTHS[p.m - 1]} ${p.y}`;
}

/** "Fri 10 Oct 2026" */
export function formatWeekdayDate(value: DateLike, empty = EMPTY): string {
  const p = dateParts(value);
  if (!p) return typeof value === 'string' && value ? value : empty;
  return `${WEEKDAYS[p.dow]} ${p.d} ${MONTHS[p.m - 1]} ${p.y}`;
}

/** "Friday 10 October" (Home's eyebrow). */
export function formatLongDay(value: DateLike, empty = EMPTY): string {
  const p = dateParts(value);
  if (!p) return empty;
  const year = p.y === currentYear() ? '' : ` ${p.y}`;
  return `${WEEKDAYS_LONG[p.dow]} ${p.d} ${MONTHS_LONG[p.m - 1]}${year}`;
}

/** "Fri 10 Oct", with the year when it isn't this year ("Fri 10 Oct 2025"). */
export function formatDayMonth(value: DateLike, empty = EMPTY, now: Date = new Date()): string {
  const p = dateParts(value);
  if (!p) return typeof value === 'string' && value ? value : empty;
  const year = p.y === currentYear(now) ? '' : ` ${p.y}`;
  return `${WEEKDAYS[p.dow]} ${p.d} ${MONTHS[p.m - 1]}${year}`;
}

/** "10 Oct", with the year when it isn't this year. */
export function formatShortDate(value: DateLike, empty = EMPTY, now: Date = new Date()): string {
  const p = dateParts(value);
  if (!p) return typeof value === 'string' && value ? value : empty;
  const year = p.y === currentYear(now) ? '' : ` ${p.y}`;
  return `${p.d} ${MONTHS[p.m - 1]}${year}`;
}

/** "Oct 2026" from a date or a "YYYY-MM" month. */
export function formatMonthYear(value: DateLike, empty = EMPTY): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) value = `${value}-01`;
  const p = dateParts(value);
  if (!p) return empty;
  return `${MONTHS[p.m - 1]} ${p.y}`;
}

/** "October 2026" from a date or a "YYYY-MM" month. */
export function formatMonthYearLong(value: DateLike, empty = EMPTY): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) value = `${value}-01`;
  const p = dateParts(value);
  if (!p) return empty;
  return `${MONTHS_LONG[p.m - 1]} ${p.y}`;
}

/** "14:05" (London time for instants). */
export function formatTime(value: DateLike, empty = EMPTY): string {
  const p = dateParts(value);
  if (!p || !p.hasTime) return empty;
  return `${pad(p.hh)}:${pad(p.mm)}`;
}

/** "10 Oct 2026, 14:05" (London time). A plain date has no time part. */
export function formatDateTime(value: DateLike, empty = EMPTY): string {
  const p = dateParts(value);
  if (!p) return empty;
  const date = `${p.d} ${MONTHS[p.m - 1]} ${p.y}`;
  return p.hasTime ? `${date}, ${pad(p.hh)}:${pad(p.mm)}` : date;
}

/** "Today, 07:52" / "Tue 6 Oct, 13:10" / "Mon 2 Mar 2025" (older years drop the time). */
export function formatWhen(value: DateLike, empty = EMPTY, now: Date = new Date()): string {
  const p = dateParts(value);
  if (!p) return empty;
  const time = p.hasTime ? `${pad(p.hh)}:${pad(p.mm)}` : '';
  const today = dateParts(now)!;
  if (p.y === today.y && p.m === today.m && p.d === today.d)
    return time ? `Today, ${time}` : 'Today';
  if (p.y !== today.y) return `${WEEKDAYS[p.dow]} ${p.d} ${MONTHS[p.m - 1]} ${p.y}`;
  const day = `${WEEKDAYS[p.dow]} ${p.d} ${MONTHS[p.m - 1]}`;
  return time ? `${day}, ${time}` : day;
}

/**
 * "Mon 19 – Fri 23 Oct 2026", "Tue 29 Dec 2026 – Fri 2 Jan 2027", one day collapses to
 * "Mon 19 Oct 2026". `withYear: 'auto'` drops the year when both ends are this year.
 */
export function formatDateRange(
  start: DateLike,
  end: DateLike,
  opts: { withYear?: boolean | 'auto'; weekday?: boolean } = {},
): string {
  const a = dateParts(start);
  const b = dateParts(end ?? start);
  if (!a || !b) return EMPTY;
  const { withYear = true, weekday = true } = opts;
  const showYear = withYear === 'auto' ? a.y !== currentYear() || b.y !== currentYear() : withYear;
  const day = (p: Parts) => `${weekday ? `${WEEKDAYS[p.dow]} ` : ''}${p.d}`;
  const full = (p: Parts) => `${day(p)} ${MONTHS[p.m - 1]}${showYear ? ` ${p.y}` : ''}`;
  if (a.y === b.y && a.m === b.m && a.d === b.d) return full(a);
  if (a.y === b.y && a.m === b.m) return `${day(a)} – ${full(b)}`;
  if (a.y === b.y) return `${day(a)} ${MONTHS[a.m - 1]} – ${full(b)}`;
  const withY = (p: Parts) => `${day(p)} ${MONTHS[p.m - 1]} ${p.y}`;
  return `${withY(a)} – ${withY(b)}`;
}

/** Local calendar day as "YYYY-MM-DD" (for API calls and inputs, never for display). */
export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today as "YYYY-MM-DD" on the user's clock. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now);
}

/** Midnight (local) of the same day. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Local Date for a "YYYY-MM-DD" (pickers and weekday maths). */
export function isoToDate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

// Numbers -------------------------------------------------------------------

type Num = number | string | null | undefined;

function toNumber(value: Num): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** "30,591", or with fixed decimals: formatNumber(12.5, 2) → "12.50". */
export function formatNumber(value: Num, decimals = 0, empty = EMPTY): string {
  const n = toNumber(value);
  if (n === null) return empty;
  return n.toLocaleString('en-GB', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Hours to 2 dp with thousands separators: 30591.36 → "30,591.36", 7.5 → "7.50". */
export function formatHours(value: Num, empty = EMPTY): string {
  return formatNumber(value, 2, empty);
}

/** Like `formatHours` but zero is the empty mark (sparse tables). */
export function formatHoursOrEmpty(value: Num, empty = EMPTY): string {
  const n = toNumber(value);
  if (n === null || n === 0) return empty;
  return formatHours(n);
}

/** Minutes as hours to 2 dp: 450 → "7.50". */
export function formatMinutesAsHours(minutes: Num, empty = EMPTY): string {
  const n = toNumber(minutes);
  if (n === null) return empty;
  return formatHours(n / 60);
}

/** "£1,250.00"; `whole` drops the pence ("£18,240"). */
export function formatMoney(value: Num, opts: { whole?: boolean } = {}, empty = EMPTY): string {
  const n = toNumber(value);
  if (n === null) return empty;
  const dp = opts.whole ? 0 : 2;
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
}

/** 83.4 → "83%", formatPercent(0.834, { ratio: true }) → "83%". */
export function formatPercent(
  value: Num,
  opts: { ratio?: boolean; decimals?: number } = {},
  empty = EMPTY,
): string {
  const n = toNumber(value);
  if (n === null) return empty;
  const pct = opts.ratio ? n * 100 : n;
  return `${formatNumber(pct, opts.decimals ?? 0)}%`;
}

/** "1 day", "2.5 days", "0.5 days". */
export function formatDays(value: Num, empty = EMPTY): string {
  const n = toNumber(value);
  if (n === null) return empty;
  const text = Number.isInteger(n) ? formatNumber(n) : formatNumber(n, 1).replace(/\.0$/, '');
  return `${text} ${n === 1 ? 'day' : 'days'}`;
}

/** "3 vehicles" / "1 vehicle"; pass the plural for irregular nouns. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

// Names and codes live in @aroo/shared so the API sends the same text; re-exported here so
// screens have one import for display formatting.
export {
  formatCode,
  formatPersonName,
  formatSiteName,
  humaniseCode,
  personDisplayName,
} from '@aroo/shared';
